import { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { client } from '@/services/api/axios.ts';

export type TEvent = {
  eventId: string;
  createdAt: string;
  type: string;
  description: string;
  source: string;
  sourceId: string;
};

interface EventsParams {
  limit?: number;
}

interface PageParam {
  nextCursor?: string;
  currentPage: number;
}

export interface EventsResponse {
  length: number;
  body: TEvent[];
  nextPage?: number | null;
  nextCursor?: string | null;
  currentPage?: number;
}

async function fetchEvents(
  pageParam: PageParam,
  { limit = 10 }: EventsParams
): Promise<EventsResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    currentPage: String(pageParam.currentPage),
    ...(pageParam.nextCursor && { nextCursor: pageParam.nextCursor }),
  });

  try {
    const { data } = await client.get<EventsResponse>(`/events?${params}`);
    return data;
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message
      : error instanceof Error
        ? error.message
        : 'An unknown error occurred';
    throw new Error(message);
  }
}

export function useGetEventsQuery(
  options: EventsParams = { limit: 10 },
  config?: UseInfiniteQueryOptions<EventsResponse, Error, TEvent[], string[], PageParam>
) {
  return useInfiniteQuery({
    ...config,
    queryKey: ['events'],
    queryFn: ({ pageParam }) => fetchEvents(pageParam, options),
    initialPageParam: { nextCursor: undefined, currentPage: 0 },
    getNextPageParam: (lastPage) =>
      lastPage?.nextPage && lastPage?.nextCursor
        ? { nextCursor: lastPage.nextCursor, currentPage: lastPage.nextPage }
        : undefined,
    select: (data) => ({
      ...data,
      pages: data.pages.flatMap((page) => page.body),
      isLastPage: !Boolean(data.pages.at(-1)?.nextCursor),
    }),
    staleTime: 60_000,
  });
}
